import React, { useRef } from 'react';
import { ApplicationView as view } from '@aha-app/mvc';
import DroppableItem from './DroppableItem';
import SortableItem, { type SortableItemProps } from './SortableItem';
import { type ItemComponentProps } from './types';
import DragPreviewItem, { type DragPreviewItemProps } from './DragPreviewItem';
import type DraggableItem from '../DraggableItem';
import { type SelectableItemController } from '../SelectableItem';
import type ListDropHandler from '../ListDropHandler';
import { sharedDragController } from '../controllers/DragController';
import ItemPlacement, {
  type ItemInterface,
} from 'components/framework/models/ItemPlacement';
import { useObservedState } from 'components/framework/hooks/useObservedState';
import { type ListLayout } from '../DropHandler';

type ItemListEntryCommonData = {
  Component: React.ComponentType;
  key: string;
};

type ItemListEntryItemData = {
  type: 'Item';
  dragItem: DraggableItem;
  props: SortableItemProps;
} & ItemListEntryCommonData;

type ItemListEntryPreviewData = {
  type: 'Preview';
  dragItem: DraggableItem;
  props: DragPreviewItemProps;
} & ItemListEntryCommonData;

type ItemListEntryCustomData = {
  type: 'Custom';
  props?: object;
} & ItemListEntryCommonData;

export type ItemListEntry =
  | ItemListEntryItemData
  | ItemListEntryPreviewData
  | ItemListEntryCustomData;

type ItemListProps = {
  // A callback to create a DraggableItem.
  createDraggableItem: (
    item: any
  ) => DraggableItem<any, SelectableItemController>;
  // A callback to create a DropHandler.
  createDropHandler?: (item: any) => ListDropHandler<SelectableItemController>;
  // Whether to hide the items while they're being dragged
  hideDragPreviews?: boolean;
  // Advise drop placement algorithim on how to use the cursor when positioning dragged items
  listLayout?: ListLayout;
  // An array of items to render in the list.
  items: object[];
  // A function that injects items into the list, useful for showing over limit lines, etc.
  // The function should mutate the passed array.
  listInjector?: (listEntries: ItemListEntry[]) => void;
  // A unique key for the list the item belongs to.
  listKey: string;
  // A component used to render the individual items.
  ItemComponent: React.ComponentType<ItemComponentProps>;

  // Class name to apply styles to the div that wraps each
  //  row in the item list
  draggableClassName?: string;
};

function loadCachedItem<T extends object, C>(
  cache: WeakMap<T, C>,
  item: T,
  builder: (item: T) => C
): C {
  if (!cache.has(item)) {
    cache.set(item, builder(item));
  }

  return cache.get(item)!;
}

const ItemList: React.FC<ItemListProps> = ({
  createDraggableItem,
  createDropHandler = null,
  hideDragPreviews = true,
  listLayout = 'vertical',
  items,
  listInjector = null,
  listKey = null,
  ItemComponent,
  draggableClassName = null,
}) => {
  const droppable = listKey && typeof createDropHandler === 'function';
  const ItemWrapper = droppable ? DroppableItem : SortableItem;
  const dragItemCache = useRef(new WeakMap());
  const dropHandlerCache = useRef(new WeakMap());
  const nullDropHandler = useRef(
    typeof createDropHandler === 'function'
      ? createDropHandler(null)
      : undefined
  );

  /**
   * Get the hover placement of the current drag. `useObservedState` will avoid unnecessary
   * re-renders while hovering over other `listKey`s.
   */
  const placement = useObservedState<ItemPlacement | null>(() => {
    const { listKey: hoverListKey } = sharedDragController.state.dropData;

    if (hoverListKey === listKey) {
      return (
        sharedDragController.state.dropData.placement || ItemPlacement.bottom()
      );
    }

    return null;
  });

  let numPreviewItems = 0;
  let indexPreviewPlacement: number;
  if (placement && droppable) {
    indexPreviewPlacement = placement.indexForList(items as ItemInterface[]);
    numPreviewItems = sharedDragController.dragItems.length;
  }

  const entries = [...items].map<ItemListEntry>((item, index) => {
    const dragItem = loadCachedItem<object, DraggableItem<any>>(
      dragItemCache.current,
      item,
      item => createDraggableItem(item)
    );
    dragItem.listKey = listKey ?? '';
    dragItem.listIndex = index;
    dragItem.listIndexWithPreviews =
      indexPreviewPlacement !== undefined &&
      indexPreviewPlacement <= dragItem.listIndex
        ? dragItem.listIndex + numPreviewItems
        : dragItem.listIndex;

    let dropHandler: ListDropHandler<SelectableItemController> | undefined;
    if (droppable) {
      dropHandler = loadCachedItem<object, ListDropHandler>(
        dropHandlerCache.current,
        item,
        item => createDropHandler(item)
      );
    }

    return {
      type: 'Item',
      dragItem,
      Component: ItemWrapper,
      key: dragItem.id,
      props: {
        dragging: sharedDragController.isDraggingDraggableItem(dragItem),
        dragItem,
        dropHandler,
        listKey,
        hideDragPreviews,
        listLayout,
        ItemComponent,
        draggableClassName,
      },
    } as ItemListEntry;
  });

  // Place preview items
  if (placement && droppable) {
    const hoverIndex = placement.indexForList(items as ItemInterface[]);
    const { dragItems } = sharedDragController;
    const currentNullDropHandler = nullDropHandler.current;

    if (currentNullDropHandler) {
      const previewDragItems = dragItems.map<ItemListEntry>(dragItem => ({
        type: 'Preview',
        dragItem,
        Component: DragPreviewItem,
        key: `DragPreviewItem-${dragItem.id}`,
        props: {
          dragItem,
          dropHandler: currentNullDropHandler,
        },
      }));

      entries.splice(hoverIndex, 0, ...previewDragItems);
    }
  }

  // Inject custom items, if needed
  if (typeof listInjector === 'function') {
    listInjector(entries);
  }

  return (
    <>
      {entries.map(({ Component, key, props }) => (
        <Component key={key} {...props} />
      ))}
    </>
  );
};

export default view(ItemList);
